<template>
  <div class="stats-view">
    <!-- Dateline -->
    <div class="stats-dateline">
      <div class="stats-dateline__inner">
        <span>MLB Desk</span>
        <span>
          Leaderboard
          <span class="stats-dateline__dot">♦</span>
          Season to Date
        </span>
        <span class="stats-dateline__right">Sorted by the Numbers</span>
      </div>
    </div>

    <!-- Editorial Hero -->
    <header class="stats-hero">
      <p class="eyebrow">The Standings Desk</p>
      <h1 class="stats-hero__title">Leaderboard <em>&mdash;</em> Season to Date</h1>
      <p class="stats-hero__lede">
        Every qualifying hitter and pitcher in the modern era, ranked on the metric of your
        choosing. Filter the field, re-sort the column, and the table reads back like the agate type
        of an old broadsheet.
      </p>
      <hr class="rule rule--double" />
    </header>

    <!-- I. Filters -->
    <section class="sect" aria-label="Leaderboard Filters">
      <div class="sect__header">
        <div class="section-number">I</div>
        <div class="sect__titles">
          <p class="eyebrow">The Field &mdash; Filters &amp; Splits</p>
          <h2 class="display-md">Choose your <em>cut</em> of the season.</h2>
        </div>
      </div>
      <hr class="rule" />

      <div class="paper-card filters-card">
        <div class="filters">
          <!-- Stat type toggle -->
          <div class="field">
            <label class="field__label">Stat Type</label>
            <div class="toggle-group" role="group" aria-label="Stat type">
              <button
                class="toggle-btn"
                :class="{ 'toggle-btn--active': statType === 'batting' }"
                :aria-pressed="statType === 'batting'"
                @click="setStatType('batting')"
              >
                Batting
              </button>
              <button
                class="toggle-btn"
                :class="{ 'toggle-btn--active': statType === 'pitching' }"
                :aria-pressed="statType === 'pitching'"
                @click="setStatType('pitching')"
              >
                Pitching
              </button>
            </div>
          </div>

          <!-- Year -->
          <div class="field">
            <label class="field__label" for="filter-year">Year</label>
            <input
              id="filter-year"
              v-model="filters.year"
              type="text"
              class="field__input field__input--sm"
              placeholder="2024 or 2020-2024"
              aria-describedby="filter-year-hint"
            />
            <span id="filter-year-hint" class="field__hint">Single year or range</span>
          </div>

          <!-- Split -->
          <div class="field">
            <label class="field__label" for="filter-split">Split</label>
            <select id="filter-split" v-model="filters.split" class="field__input field__input--sm">
              <option value="total">Total</option>
              <option value="home">Home</option>
              <option value="away">Away</option>
              <template v-if="statType === 'batting'">
                <option value="vs_lhp">vs LHP</option>
                <option value="vs_rhp">vs RHP</option>
              </template>
              <template v-else>
                <option value="vs_lhb">vs LHB</option>
                <option value="vs_rhb">vs RHB</option>
              </template>
            </select>
          </div>

          <!-- Min PA / Min IP -->
          <div v-if="statType === 'batting'" class="field">
            <label class="field__label" for="filter-min-pa">Min PA</label>
            <input
              id="filter-min-pa"
              v-model.number="filters.min_pa"
              type="number"
              min="0"
              class="field__input field__input--sm"
              placeholder="50"
            />
          </div>
          <div v-else class="field">
            <label class="field__label" for="filter-min-ip">Min IP</label>
            <input
              id="filter-min-ip"
              v-model.number="filters.min_ip"
              type="number"
              min="0"
              class="field__input field__input--sm"
              placeholder="20"
            />
          </div>

          <!-- Sort direction -->
          <div class="field">
            <label class="field__label" for="filter-sort-dir">Order</label>
            <select
              id="filter-sort-dir"
              v-model="filters.sort_dir"
              class="field__input field__input--sm"
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </div>

          <!-- Page size -->
          <div class="field">
            <label class="field__label" for="filter-limit">Per Page</label>
            <select
              id="filter-limit"
              v-model.number="filters.limit"
              class="field__input field__input--sm"
            >
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>

          <button class="btn-press filters__apply" :disabled="loading" @click="applyFilters">
            {{ loading ? 'Loading…' : 'Apply' }}
            <span class="btn-press__arrow" aria-hidden="true">&rarr;</span>
          </button>
        </div>
      </div>
    </section>

    <hr class="rule rule--ox sect__divider" />

    <!-- II. Results -->
    <section class="sect" aria-label="Leaderboard Results">
      <div class="sect__header">
        <div class="section-number">II</div>
        <div class="sect__titles">
          <p class="eyebrow">
            The Standings &mdash; {{ statType === 'batting' ? 'Batting' : 'Pitching' }}
          </p>
          <h2 class="display-md">
            {{ statType === 'batting' ? 'Batting' : 'Pitching' }} Leaders <em>&mdash;</em> the
            board.
          </h2>
          <p v-if="total > 0" class="results-count">
            {{ total.toLocaleString() }} results &middot; sorted by
            <span class="numeric results-count__key">{{ sortLabel }}</span>
          </p>
        </div>
      </div>
      <hr class="rule" />

      <!-- Error -->
      <div v-if="error" class="alert alert--error">{{ error }}</div>

      <!-- Loading skeleton -->
      <div v-else-if="loading" class="loading-state">
        <div v-for="i in 5" :key="i" class="skeleton-row"></div>
      </div>

      <!-- Batting table -->
      <div v-else-if="statType === 'batting' && battingRows.length > 0" class="scorecard">
        <div class="scorecard__scroll">
          <table class="data-table" aria-label="Batting leaderboard">
            <thead>
              <tr>
                <th class="col-rank col-fixed">Rk</th>
                <th class="col-player col-fixed">Player</th>
                <th class="col-team col-fixed">Team</th>
                <th class="num col-year col-fixed">Year</th>
                <th
                  v-for="col in battingCols"
                  :key="col.key"
                  class="num"
                  :class="{ 'col-active': filters.sort_by === col.key }"
                  :aria-sort="
                    filters.sort_by === col.key
                      ? filters.sort_dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  "
                  :title="col.label"
                  @click="sortBy(col.key)"
                >
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in battingRows"
                :key="i"
                class="row"
                :class="{ 'row--leader': filters.offset === 0 && i === 0 }"
              >
                <td class="num col-rank rank-cell">{{ filters.offset + i + 1 }}</td>
                <td class="col-player">
                  <RouterLink
                    v-if="row.player_id"
                    :to="`/players/${row.player_id}`"
                    class="player-link"
                    >{{ row.player_name || '—' }}</RouterLink
                  >
                  <span v-else class="player-name">{{ row.player_name || '—' }}</span>
                </td>
                <td class="team-cell">{{ row.team_abbreviation || '—' }}</td>
                <td class="num year-cell numeric">{{ row.season_year ?? '—' }}</td>
                <td class="num numeric">{{ fmtInt(row.games) }}</td>
                <td class="num numeric">{{ fmtInt(row.plate_appearances) }}</td>
                <td class="num numeric">{{ fmtInt(row.at_bats) }}</td>
                <td class="num numeric">{{ fmtInt(row.runs) }}</td>
                <td class="num numeric">{{ fmtInt(row.hits) }}</td>
                <td class="num numeric">{{ fmtInt(row.doubles) }}</td>
                <td class="num numeric">{{ fmtInt(row.triples) }}</td>
                <td class="num numeric">{{ fmtInt(row.home_runs) }}</td>
                <td class="num numeric">{{ fmtInt(row.rbi) }}</td>
                <td class="num numeric">{{ fmtInt(row.walks) }}</td>
                <td class="num numeric">{{ fmtInt(row.strikeouts) }}</td>
                <td class="num numeric">{{ fmtInt(row.stolen_bases) }}</td>
                <td class="num numeric">{{ fmtAvg(row.batting_avg) }}</td>
                <td class="num numeric">{{ fmtAvg(row.on_base_pct) }}</td>
                <td class="num numeric">{{ fmtAvg(row.slugging_pct) }}</td>
                <td class="num numeric col-highlight">{{ fmtAvg(row.ops) }}</td>
                <td class="num numeric">{{ fmtAvg(row.woba) }}</td>
                <td class="num numeric col-highlight">{{ fmtWrc(row.wrc_plus) }}</td>
                <td class="num numeric">{{ fmtAvg(row.babip) }}</td>
                <td class="num numeric">{{ fmtAvg(row.isolated_power) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pitching table -->
      <div v-else-if="statType === 'pitching' && pitchingRows.length > 0" class="scorecard">
        <div class="scorecard__scroll">
          <table class="data-table" aria-label="Pitching leaderboard">
            <thead>
              <tr>
                <th class="col-rank col-fixed">Rk</th>
                <th class="col-player col-fixed">Player</th>
                <th class="col-team col-fixed">Team</th>
                <th class="num col-year col-fixed">Year</th>
                <th
                  v-for="col in pitchingCols"
                  :key="col.key"
                  class="num"
                  :class="{ 'col-active': filters.sort_by === col.key }"
                  :aria-sort="
                    filters.sort_by === col.key
                      ? filters.sort_dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  "
                  :title="col.label"
                  @click="sortBy(col.key)"
                >
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in pitchingRows"
                :key="i"
                class="row"
                :class="{ 'row--leader': filters.offset === 0 && i === 0 }"
              >
                <td class="num col-rank rank-cell">{{ filters.offset + i + 1 }}</td>
                <td class="col-player">
                  <RouterLink
                    v-if="row.player_id"
                    :to="`/players/${row.player_id}`"
                    class="player-link"
                    >{{ row.player_name || '—' }}</RouterLink
                  >
                  <span v-else class="player-name">{{ row.player_name || '—' }}</span>
                </td>
                <td class="team-cell">{{ row.team_abbreviation || '—' }}</td>
                <td class="num year-cell numeric">{{ row.season_year ?? '—' }}</td>
                <td class="num numeric">{{ fmtInt(row.games) }}</td>
                <td class="num numeric">{{ fmtInt(row.games_started) }}</td>
                <td class="num numeric">{{ fmtInt(row.wins) }}</td>
                <td class="num numeric">{{ fmtInt(row.losses) }}</td>
                <td class="num numeric">{{ fmtInt(row.saves) }}</td>
                <td class="num numeric">{{ fmtDec(row.innings_pitched) }}</td>
                <td class="num numeric">{{ fmtInt(row.strikeouts) }}</td>
                <td class="num numeric">{{ fmtInt(row.walks) }}</td>
                <td class="num numeric">{{ fmtInt(row.home_runs) }}</td>
                <td class="num numeric col-highlight">{{ fmtDec(row.era) }}</td>
                <td class="num numeric col-highlight">{{ fmtDec(row.whip) }}</td>
                <td class="num numeric">{{ fmtDec(row.fip) }}</td>
                <td class="num numeric">{{ fmtDec(row.k_per_9) }}</td>
                <td class="num numeric">{{ fmtDec(row.bb_per_9) }}</td>
                <td class="num numeric">{{ fmtDec(row.k_per_bb) }}</td>
                <td class="num numeric">{{ fmtPct(row.groundball_pct) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty state -->
      <p v-else-if="!loading && !error" class="empty-state">
        <em>The wire is quiet.</em> No results match the filters as written &mdash; try widening the
        field, or loosen the qualifier.
      </p>

      <!-- Pagination -->
      <div v-if="total > 0 && !loading" class="pagination">
        <button
          class="btn-press btn-press--ghost pagination__btn"
          :disabled="filters.offset === 0"
          @click="changePage(-1)"
        >
          &larr; Prev
        </button>
        <span class="pagination__info">
          Page <span class="pagination__roman">{{ toRoman(currentPage) }}</span> of
          <span class="pagination__roman">{{ toRoman(totalPages) }}</span>
          <span class="pagination__range">
            &middot; {{ filters.offset + 1 }}&ndash;{{
              Math.min(filters.offset + filters.limit, total)
            }}
            of {{ total.toLocaleString() }}
          </span>
        </span>
        <button
          class="btn-press btn-press--ghost pagination__btn"
          :disabled="filters.offset + filters.limit >= total"
          @click="changePage(1)"
        >
          Next &rarr;
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { statsApi } from '@/api/stats'
import type { BattingStatsRow, PitchingStatsRow } from '@/api/stats'
import type { ApiError } from '@/types/api'

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

type StatType = 'batting' | 'pitching'

const statType = ref<StatType>('batting')
const loading = ref(false)
const error = ref<string | null>(null)
const total = ref(0)

const battingRows = ref<BattingStatsRow[]>([])
const pitchingRows = ref<PitchingStatsRow[]>([])

const filters = reactive({
  year: String(new Date().getFullYear() - 1),
  split: 'total',
  sort_by: 'ops',
  sort_dir: 'desc',
  min_pa: 50,
  min_ip: 20,
  limit: 50,
  offset: 0
})

// ---------------------------------------------------------------------------
// Column definitions (visible in the table header)
// ---------------------------------------------------------------------------

const battingCols = [
  { key: 'games', label: 'G' },
  { key: 'plate_appearances', label: 'PA' },
  { key: 'at_bats', label: 'AB' },
  { key: 'runs', label: 'R' },
  { key: 'hits', label: 'H' },
  { key: 'doubles', label: '2B' },
  { key: 'triples', label: '3B' },
  { key: 'home_runs', label: 'HR' },
  { key: 'rbi', label: 'RBI' },
  { key: 'walks', label: 'BB' },
  { key: 'strikeouts', label: 'K' },
  { key: 'stolen_bases', label: 'SB' },
  { key: 'batting_avg', label: 'AVG' },
  { key: 'on_base_pct', label: 'OBP' },
  { key: 'slugging_pct', label: 'SLG' },
  { key: 'ops', label: 'OPS' },
  { key: 'woba', label: 'wOBA' },
  { key: 'wrc_plus', label: 'wRC+' },
  { key: 'babip', label: 'BABIP' },
  { key: 'isolated_power', label: 'ISO' }
]

const pitchingCols = [
  { key: 'games', label: 'G' },
  { key: 'games_started', label: 'GS' },
  { key: 'wins', label: 'W' },
  { key: 'losses', label: 'L' },
  { key: 'saves', label: 'SV' },
  { key: 'innings_pitched', label: 'IP' },
  { key: 'strikeouts', label: 'K' },
  { key: 'walks', label: 'BB' },
  { key: 'home_runs', label: 'HR' },
  { key: 'era', label: 'ERA' },
  { key: 'whip', label: 'WHIP' },
  { key: 'fip', label: 'FIP' },
  { key: 'k_per_9', label: 'K/9' },
  { key: 'bb_per_9', label: 'BB/9' },
  { key: 'k_per_bb', label: 'K/BB' },
  { key: 'groundball_pct', label: 'GB%' }
]

// Pitching rate stats where lower is better — clicking a new column defaults to asc
const PITCHING_ASC_COLS = new Set([
  'era',
  'whip',
  'fip',
  'bb_per_9',
  'hr_per_9',
  'xfip',
  'twhip',
  'tera'
])

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const sortLabel = computed(() => {
  const cols = statType.value === 'batting' ? battingCols : pitchingCols
  const col = cols.find(c => c.key === filters.sort_by)
  return col ? col.label : filters.sort_by
})

const currentPage = computed((): number => Math.floor(filters.offset / filters.limit) + 1)

const totalPages = computed((): number => Math.max(1, Math.ceil(total.value / filters.limit)))

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

function setStatType(type: StatType): void {
  statType.value = type
  filters.sort_by = type === 'batting' ? 'ops' : 'era'
  filters.sort_dir = type === 'batting' ? 'desc' : 'asc'
  // Reset platoon split when switching stat type to avoid cross-type invalid splits
  if (filters.split === 'vs_lhp' || filters.split === 'vs_rhp') filters.split = 'total'
  if (filters.split === 'vs_lhb' || filters.split === 'vs_rhb') filters.split = 'total'
  filters.offset = 0
  fetchData()
}

function sortBy(col: string): void {
  if (filters.sort_by === col) {
    filters.sort_dir = filters.sort_dir === 'desc' ? 'asc' : 'desc'
  } else {
    filters.sort_by = col
    // For pitching, lower-is-better rate stats default to ascending
    filters.sort_dir = statType.value === 'pitching' && PITCHING_ASC_COLS.has(col) ? 'asc' : 'desc'
  }
  filters.offset = 0
  fetchData()
}

function applyFilters(): void {
  filters.offset = 0
  fetchData()
}

function changePage(direction: 1 | -1): void {
  filters.offset = Math.max(0, filters.offset + direction * filters.limit)
  fetchData()
}

async function fetchData(): Promise<void> {
  loading.value = true
  error.value = null

  try {
    if (statType.value === 'batting') {
      const res = await statsApi.getBatting({
        year: filters.year || undefined,
        split: filters.split,
        sort_by: filters.sort_by,
        sort_dir: filters.sort_dir,
        min_pa: filters.min_pa,
        aggregation_period: 'season',
        limit: filters.limit,
        offset: filters.offset
      })
      battingRows.value = res.data
      total.value = res.total
    } else {
      const res = await statsApi.getPitching({
        year: filters.year || undefined,
        split: filters.split,
        sort_by: filters.sort_by,
        sort_dir: filters.sort_dir,
        min_ip: filters.min_ip,
        aggregation_period: 'season',
        limit: filters.limit,
        offset: filters.offset
      })
      pitchingRows.value = res.data
      total.value = res.total
    }
  } catch (err: unknown) {
    const apiErr = err as ApiError
    // Axios wraps backend errors; fall back through possible shapes
    error.value =
      apiErr.detail ||
      (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
      'Failed to load stats'
  } finally {
    loading.value = false
  }
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmtInt(val: number | null): string {
  return val == null ? '—' : val.toLocaleString()
}

function fmtAvg(val: number | null): string {
  if (val == null) return '—'
  return val.toFixed(3).replace(/^0/, '')
}

function fmtDec(val: number | null): string {
  return val == null ? '—' : val.toFixed(2)
}

function fmtWrc(val: number | null): string {
  return val == null ? '—' : Math.round(val).toString()
}

function fmtPct(val: number | null): string {
  if (val == null) return '—'
  return (val * 100).toFixed(1) + '%'
}

// Roman numerals for editorial pagination — "Page I of VI".
function toRoman(n: number): string {
  if (n <= 0) return '0'
  const romans: Array<[number, string]> = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I']
  ]
  let out = ''
  let remaining = n
  for (const [value, symbol] of romans) {
    while (remaining >= value) {
      out += symbol
      remaining -= value
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(fetchData)
</script>

<style scoped>
.stats-view {
  min-height: 100vh;
  padding-top: 4.75rem;
  color: var(--ink);
}

/* ─── Dateline ──────────────────────────────────────────────────────── */
.stats-dateline {
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  background: var(--paper);
}

.stats-dateline__inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.55rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1.5rem;
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-medium);
  text-align: center;
}

.stats-dateline__inner > span:first-child {
  text-align: left;
}

.stats-dateline__right {
  text-align: right;
}

.stats-dateline__dot {
  display: inline-block;
  margin: 0 0.6em;
  color: var(--ox);
  transform: translateY(-1px);
}

/* ─── Editorial hero ───────────────────────────────────────────────── */
.stats-hero {
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem 1.5rem;
}

.stats-hero__title {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 30,
    'wght' 700;
  font-size: clamp(2.5rem, 6vw, 4.75rem);
  line-height: 0.95;
  letter-spacing: -0.025em;
  color: var(--ink);
  margin: 0.75rem 0 1.25rem;
  max-width: 18ch;
}

.stats-hero__title em {
  font-style: italic;
  color: var(--ox);
  font-variation-settings:
    'opsz' 144,
    'SOFT' 100,
    'wght' 500;
}

.stats-hero__lede {
  font-family: var(--font-editorial);
  font-size: 1.1rem;
  line-height: 1.55;
  color: var(--ink-medium);
  max-width: 62ch;
  margin-bottom: 2rem;
}

/* ─── Sections ──────────────────────────────────────────────────────── */
.sect {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2.5rem 2rem 3rem;
}

.sect__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1.5rem;
  align-items: start;
  margin-bottom: 0.5rem;
}

.sect__titles .eyebrow {
  margin-bottom: 0.65rem;
}

.sect__titles em {
  font-style: italic;
  color: var(--ox);
}

.sect__divider {
  max-width: 1400px;
  margin: 0 auto;
}

/* ─── Filters card ──────────────────────────────────────────────────── */
.filters-card {
  padding: 1.5rem 1.75rem;
  margin-top: 1.25rem;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 1.5rem;
  align-items: flex-end;
}

.filters__apply {
  margin-bottom: 0;
  padding: 0.75rem 1.5rem;
  font-size: 0.72rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field__label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

.field__input {
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  border-radius: 0;
  color: var(--ink);
  font-family: var(--font-data);
  font-size: 0.85rem;
  padding: 0.5rem 0.7rem;
  outline: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.field__input:focus {
  border-color: var(--ox);
  box-shadow: 2px 2px 0 var(--ink);
}

.field__input--sm {
  width: 8rem;
}

.field__hint {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-ghost);
}

/* ─── Toggle group ──────────────────────────────────────────────────── */
.toggle-group {
  display: inline-flex;
  border: var(--rule-thin) solid var(--ink);
  background: var(--paper);
}

.toggle-btn {
  font-family: var(--font-ui);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0.55rem 1rem;
  background: var(--paper);
  color: var(--ink-medium);
  border: none;
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease;
}

.toggle-btn + .toggle-btn {
  border-left: var(--rule-thin) solid var(--ink);
}

.toggle-btn:hover {
  color: var(--ink);
  background: var(--paper-deep);
}

.toggle-btn--active,
.toggle-btn--active:hover {
  background: var(--ink);
  color: var(--paper);
}

.btn-press__arrow {
  display: inline-block;
  transition: transform 0.18s ease;
}

.btn-press:hover .btn-press__arrow {
  transform: translateX(4px);
}

.btn-press:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: var(--ink);
  transform: none;
  box-shadow: var(--shadow-ink);
}

/* ─── Results header ────────────────────────────────────────────────── */
.results-count {
  font-family: var(--font-ui);
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: 0.85rem;
}

.results-count__key {
  color: var(--ox);
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

/* ─── Scorecard wrapper ─────────────────────────────────────────────── */
.scorecard {
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  box-shadow: var(--shadow-press);
  margin-top: 1.25rem;
}

.scorecard__scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ─── Data table ────────────────────────────────────────────────────── */
.data-table {
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  background: var(--paper);
  font-family: var(--font-editorial);
  font-size: 0.9rem;
  color: var(--ink);
}

.data-table thead {
  background: var(--paper-deep);
  border-bottom: var(--rule-med) solid var(--ink);
}

.data-table th {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-medium);
  padding: 0.7rem 0.65rem;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  text-align: left;
  transition:
    color 0.15s ease,
    background 0.15s ease;
}

.data-table th.num {
  text-align: right;
}

.data-table th.col-fixed {
  cursor: default;
}

.data-table th:not(.col-fixed):hover {
  color: var(--ink);
  background: var(--paper-edge);
}

.data-table th.col-active {
  color: var(--ox);
}

.data-table tbody tr {
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
  transition: background 0.15s ease;
}

.data-table tbody tr:last-child {
  border-bottom: none;
}

.data-table tbody tr:hover td {
  background: var(--paper-deep);
}

.data-table td {
  padding: 0.55rem 0.65rem;
  color: var(--ink);
  white-space: nowrap;
  vertical-align: baseline;
}

.col-rank {
  width: 3rem;
}

.col-player {
  min-width: 13rem;
}

.col-team {
  width: 4.5rem;
}

.col-year {
  width: 4.5rem;
}

.rank-cell {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 500;
  font-style: italic;
  font-size: 1.05rem;
  color: var(--ink-faint);
}

.team-cell {
  font-family: var(--font-data);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  text-transform: uppercase;
}

.year-cell {
  color: var(--ink-faint);
  font-size: 0.85rem;
}

.col-highlight {
  color: var(--ox);
  font-weight: 600;
}

.player-name {
  font-family: var(--font-editorial);
  font-weight: 600;
  color: var(--ink);
}

.player-link {
  font-family: var(--font-editorial);
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  border-bottom: var(--rule-thin) solid transparent;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.player-link:hover {
  color: var(--ox);
  border-bottom-color: var(--ox);
}

.num {
  text-align: right;
}

/* Leader row: gold-tint band with ox-blood ruled left edge */
.row--leader td {
  background: rgba(184, 138, 62, 0.16);
  box-shadow: inset 4px 0 0 var(--ox);
}

.row--leader:hover td {
  background: rgba(184, 138, 62, 0.24);
}

.row--leader .rank-cell {
  color: var(--ox);
}

/* ─── Loading / error / empty ───────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 1rem 0;
}

.skeleton-row {
  height: 2.25rem;
  background: linear-gradient(
    90deg,
    var(--paper-deep) 25%,
    var(--paper-edge) 50%,
    var(--paper-deep) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border: var(--rule-thin) solid var(--paper-edge);
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.alert {
  padding: 0.9rem 1.15rem;
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  margin: 1rem 0;
  border: var(--rule-thin) solid var(--ox);
  background: var(--ox-10);
  color: var(--ox-deep);
}

.empty-state {
  font-family: var(--font-editorial);
  font-style: italic;
  font-size: 1.05rem;
  color: var(--ink-faint);
  text-align: center;
  padding: 3rem 1rem;
  max-width: 54ch;
  margin: 0 auto;
}

.empty-state em {
  color: var(--ox);
  font-style: italic;
}

/* ─── Pagination ────────────────────────────────────────────────────── */
.pagination {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.pagination__btn {
  padding: 0.7rem 1.2rem;
  font-size: 0.7rem;
}

.pagination__btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  background: transparent;
  color: var(--ink);
}

.pagination__info {
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-medium);
  text-align: center;
}

.pagination__roman {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 600;
  font-style: italic;
  font-size: 1.1rem;
  letter-spacing: 0;
  color: var(--ox);
  margin: 0 0.2em;
}

.pagination__range {
  display: block;
  margin-top: 0.25rem;
  font-family: var(--font-data);
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
}

/* ─── Responsive ────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .stats-hero {
    padding: 2.25rem 1.25rem 1rem;
  }
  .sect {
    padding: 2rem 1.25rem;
  }
  .sect__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 700px) {
  .stats-dateline__inner {
    grid-template-columns: 1fr;
    gap: 0.3rem;
    text-align: center;
  }
  .stats-dateline__inner > span:first-child,
  .stats-dateline__right {
    text-align: center;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }
  .field__input--sm {
    width: 100%;
  }
  .toggle-group {
    width: 100%;
  }
  .toggle-btn {
    flex: 1;
  }
  .filters__apply {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 420px) {
  .stats-hero__title {
    font-size: 2.25rem;
  }
  .sect {
    padding: 1.75rem 1rem;
  }
  .filters-card {
    padding: 1.15rem 1rem;
  }
}
</style>
